import { Button, Dialog, DialogContent, DialogHeader, DialogTitle } from '@commander/ui';
import type React from 'react';
import { useState } from 'react';

interface DynamicsUrlModalProps {
  show: boolean;
  onClose: () => void;
  onConnect: (cleanUrl: string) => void;
}

export default function DynamicsUrlModal({ show, onClose, onConnect }: DynamicsUrlModalProps) {
  const [dynamicsUrl, setDynamicsUrl] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Basic URL validation
    if (!dynamicsUrl.trim()) {
      setError('Please enter your Dynamics 365 URL');
      return;
    }

    // Clean up: strip trailing slash
    const cleanUrl = dynamicsUrl.trim().replace(/\/+$/, '');

    // Validate URL format (hostname only, no protocol — mask handles that)
    const urlRegex = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!urlRegex.test(cleanUrl)) {
      setError('Please enter a valid Dynamics 365 URL (e.g., yourcompany.crm.dynamics.com)');
      return;
    }

    onConnect(cleanUrl);
  };

  const handleClose = () => {
    setDynamicsUrl('');
    setError('');
    onClose();
  };

  return (
    <Dialog open={show} onOpenChange={handleClose}>
      <DialogContent className="max-w-md z-[99999]">
        <DialogHeader>
          <DialogTitle>Connect Microsoft Dynamics 365</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="dynamicsUrl" className="block text-sm font-medium text-gray-700 mb-1">
              Dynamics 365 URL
            </label>
            <div className="flex">
              <span className="inline-flex items-center px-3 py-2 border border-r-0 border-gray-300 rounded-l-md bg-gray-50 text-gray-500 text-sm">
                https://
              </span>
              <input
                type="text"
                id="dynamicsUrl"
                value={dynamicsUrl}
                onChange={(e) => {
                  let val = e.target.value;
                  if (val.startsWith('https://')) val = val.slice(8);
                  else if (val.startsWith('http://')) val = val.slice(7);
                  setDynamicsUrl(val);
                }}
                placeholder="yourcompany.crm.dynamics.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-r-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Enter your Microsoft Dynamics 365 instance URL
            </p>
            {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
          </div>

          <div className="flex gap-3 justify-end">
            <Button type="button" variant="outline" onClick={handleClose} className="px-4 py-2">
              Cancel
            </Button>
            <Button type="submit" className="px-4 py-2 bg-primary text-white hover:bg-primary/90">
              Connect
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
